import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Wrench, Paintbrush, Zap, Disc, Calendar, FileCheck, MoreHorizontal,
  TrendingUp, TrendingDown, Minus as MinusIcon, Pencil, Trash2, X, Search,
  PackagePlus, User, Building2, BarChart3, Check, ChevronDown,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { WORKSHOP_CATEGORIES, INTERVENTI_BY_CATEGORIA, RICAMBI_COMUNI } from "../../lib/workshopData";
import SearchableSelect from "../../components/SearchableSelect";
import DocumentScannerBox from "../../components/DocumentScannerBox";

const ICON_MAP = { Wrench, Paintbrush, Zap, Disc, Calendar, FileCheck, MoreHorizontal };

const STATO_BADGE = {
  programmata: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  in_corso: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  completata: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  annullata: "bg-white/10 text-white/40 border-white/20",
};

const emptyRicambio = { nome: "", codice: "", quantita: 1, prezzo_unitario: 0, fornitore: "", note: "" };

const emptyForm = {
  car_id: "",
  categoria: "meccanica",
  intervento: "",        // stringa concatenata di tutti gli interventi selezionati
  interventi_list: [],   // array di chip selezionati (UI state)
  descrizione: "",
  costo_manodopera: 0,
  costo_totale: 0,
  operatore_nome: "",
  officina: "",
  km_intervento: 0,
  ricambi: [],
  data: new Date().toISOString().slice(0, 10),
  data_completamento: "",
  fattura_numero: "",
  stato: "completata",
  note: "",
  garanzia_mesi: 0,
  garanzia_km: 0,
};

export default function Workshop() {
  const [items, setItems] = useState([]);
  const [cars, setCars] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPartsHistory, setShowPartsHistory] = useState(false);
  const [partsHistory, setPartsHistory] = useState(null);
  const [partsSearch, setPartsSearch] = useState("");
  // sottosezione espansa per categoria selezionata (default: tutte aperte)
  const [openSections, setOpenSections] = useState({});
  const [chipSearch, setChipSearch] = useState("");

  const load = async () => {
    const [m, c] = await Promise.all([api.get("/maintenance"), api.get("/cars")]);
    setItems(m.data); setCars(c.data);
  };
  useEffect(() => { load(); }, []);

  const loadPartsHistory = async () => {
    const r = await api.get("/maintenance/parts-history", { params: partsSearch ? { nome: partsSearch } : {} });
    setPartsHistory(r.data);
  };

  // Statistiche
  const stats = useMemo(() => {
    const byCategory = {};
    let totalSpent = 0, totalParts = 0;
    WORKSHOP_CATEGORIES.forEach((c) => { byCategory[c.value] = { count: 0, total: 0 }; });
    items.forEach((m) => {
      const cat = m.categoria || m.tipo || "altro";
      if (!byCategory[cat]) byCategory[cat] = { count: 0, total: 0 };
      byCategory[cat].count += 1;
      byCategory[cat].total += +(m.costo_totale || m.costo || 0);
      totalSpent += +(m.costo_totale || m.costo || 0);
      totalParts += (m.ricambi || []).filter((r) => typeof r === "object").length;
    });
    return { byCategory, totalSpent, totalParts };
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((m) => {
      if (activeTab !== "all" && (m.categoria || m.tipo) !== activeTab) return false;
      if (search) {
        const s = search.toLowerCase();
        return [m.intervento, m.descrizione, m.operatore_nome, m.officina, m.car?.targa, m.car?.modello]
          .filter(Boolean).some((x) => String(x).toLowerCase().includes(s));
      }
      return true;
    });
  }, [items, activeTab, search]);

  const totaleRicambi = useMemo(() => {
    return (form.ricambi || []).reduce((s, r) => s + (+r.quantita || 0) * (+r.prezzo_unitario || 0), 0);
  }, [form.ricambi]);

  useEffect(() => {
    const t = +form.costo_manodopera + totaleRicambi;
    setForm((f) => ({ ...f, costo_totale: +t.toFixed(2) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.costo_manodopera, totaleRicambi]);

  // Toggle chip intervento
  const toggleIntervento = (intervento) => {
    setForm((f) => {
      const list = f.interventi_list.includes(intervento)
        ? f.interventi_list.filter((x) => x !== intervento)
        : [...f.interventi_list, intervento];
      return {
        ...f,
        interventi_list: list,
        intervento: list.join(" · "),  // stringa concatenata salvata sul backend
      };
    });
  };

  // Categoria change → reset chip selezionati per coerenza
  const onCategoriaChange = (categoria) => {
    setForm((f) => ({ ...f, categoria, interventi_list: [], intervento: "" }));
    setChipSearch("");
    setOpenSections({});
  };

  const toggleSection = (key) =>
    setOpenSections((o) => ({ ...o, [key]: !o[key] }));  // default CLOSED (search-first UX)

  const submit = async (e) => {
    e.preventDefault();
    if (!form.car_id) return toast.error("Seleziona un veicolo");
    if (!form.intervento) return toast.error("Seleziona almeno un intervento eseguito (clic sui chip)");
    try {
      const payload = {
        ...form,
        costo_manodopera: +form.costo_manodopera,
        costo_totale: +form.costo_totale,
        km_intervento: +(form.km_intervento || 0),
        garanzia_mesi: +(form.garanzia_mesi || 0),
        garanzia_km: +(form.garanzia_km || 0),
        ricambi: (form.ricambi || []).map((r) => ({
          ...r, quantita: +r.quantita, prezzo_unitario: +r.prezzo_unitario,
        })),
      };
      delete payload.interventi_list;  // UI-only
      if (editId) await api.put(`/maintenance/${editId}`, payload);
      else await api.post("/maintenance", payload);
      toast.success(editId ? "Intervento aggiornato" : "Intervento registrato");
      setShowForm(false); setForm(emptyForm); setEditId(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const startEdit = (m) => {
    const cat = m.categoria || m.tipo || "meccanica";
    // tenta di ricostruire la lista chip dal campo intervento (separato da " · ")
    const list = (m.intervento || "").split(" · ").filter(Boolean);
    setForm({
      ...emptyForm, ...m,
      categoria: cat,
      interventi_list: list,
      ricambi: (m.ricambi || []).map((r) => typeof r === "string" ? { ...emptyRicambio, nome: r } : r),
    });
    setEditId(m.id);
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo intervento?")) return;
    await api.delete(`/maintenance/${id}`);
    toast.success("Intervento eliminato"); load();
  };

  const addRicambio = () => setForm({ ...form, ricambi: [...(form.ricambi || []), { ...emptyRicambio }] });
  const updateRicambio = (i, k, v) => {
    const arr = [...form.ricambi];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, ricambi: arr });
  };
  const removeRicambio = (i) => setForm({ ...form, ricambi: form.ricambi.filter((_, idx) => idx !== i) });

  // Interventi della categoria corrente (con filtro search chip)
  const categoryInterventi = INTERVENTI_BY_CATEGORIA[form.categoria] || {};
  const ricambiSuggestions = RICAMBI_COMUNI[form.categoria] || [];
  const chipSearchLower = chipSearch.toLowerCase().trim();
  const filteredSections = useMemo(() => {
    if (!chipSearchLower) return categoryInterventi;
    const filtered = {};
    Object.entries(categoryInterventi).forEach(([section, list]) => {
      const matches = list.filter((x) => x.toLowerCase().includes(chipSearchLower));
      if (matches.length) filtered[section] = matches;
    });
    return filtered;
  }, [categoryInterventi, chipSearchLower]);

  return (
    <div className="space-y-6" data-testid="workshop-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Officina</h1>
          <p className="text-sm text-white/45 mt-1">
            {items.length} interventi totali · Spesa <b className="text-[#ff5577]">€ {stats.totalSpent.toFixed(2)}</b> · {stats.totalParts} ricambi tracciati
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setShowPartsHistory(true); loadPartsHistory(); }}
            data-testid="parts-history-btn"
            className="px-4 py-2 rounded-full text-xs uppercase tracking-widest bg-white/[0.04] border border-white/10 hover:bg-white/10 inline-flex items-center gap-2">
            <BarChart3 size={13} /> Storico Ricambi
          </button>
          <button onClick={() => { setForm(emptyForm); setEditId(null); setOpenSections({}); setShowForm(true); }}
            data-testid="workshop-add"
            className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Nuovo Intervento
          </button>
        </div>
      </div>

      {/* Stats per categoria */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <button onClick={() => setActiveTab("all")}
          className={`p-3 rounded-xl border transition-all text-left ${activeTab === "all" ? "border-[#ff0033] bg-[#ff0033]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
          <div className="text-[10px] uppercase tracking-widest text-white/45">Tutti</div>
          <div className="font-display text-xl font-bold mt-1">{items.length}</div>
          <div className="text-[10px] text-white/40">€ {stats.totalSpent.toFixed(0)}</div>
        </button>
        {WORKSHOP_CATEGORIES.map((cat) => {
          const Ico = ICON_MAP[cat.icon] || Wrench;
          const s = stats.byCategory[cat.value] || { count: 0, total: 0 };
          const active = activeTab === cat.value;
          return (
            <button key={cat.value} onClick={() => setActiveTab(cat.value)}
              data-testid={`cat-tab-${cat.value}`}
              className={`p-3 rounded-xl border transition-all text-left ${active ? "border-[#ff0033] bg-[#ff0033]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
              <Ico size={14} style={{ color: cat.color }} />
              <div className="text-[10px] uppercase tracking-widest text-white/45 mt-1 truncate">{cat.label}</div>
              <div className="font-display text-lg font-bold">{s.count}</div>
              <div className="text-[10px] text-white/40">€ {s.total.toFixed(0)}</div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input data-testid="workshop-search" placeholder="Cerca intervento, targa, operatore…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
      </div>

      {/* Lista interventi */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-white/10 p-10 text-center text-white/40">
            Nessun intervento {activeTab !== "all" ? `nella categoria ${activeTab}` : ""}
          </div>
        )}
        {filtered.map((m, i) => {
          const cat = WORKSHOP_CATEGORIES.find((c) => c.value === (m.categoria || m.tipo)) || WORKSHOP_CATEGORIES[0];
          const Ico = ICON_MAP[cat.icon] || Wrench;
          const ricambiCount = (m.ricambi || []).filter((r) => typeof r === "object").length;
          // numero di interventi chip
          const interventiList = (m.intervento || "").split(" · ").filter(Boolean);
          return (
            <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className="glass rounded-2xl p-4 border border-white/5 hover:border-[#ff0033]/40 transition-all"
              data-testid={`maint-${m.id}`}>
              <div className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-12 md:col-span-1 flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ background: `${cat.color}20`, borderColor: `${cat.color}50` }}>
                    <Ico size={16} style={{ color: cat.color }} />
                  </div>
                </div>
                <div className="col-span-12 md:col-span-5">
                  {interventiList.length > 1 ? (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {interventiList.slice(0, 3).map((iv, k) => (
                        <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10">{iv}</span>
                      ))}
                      {interventiList.length > 3 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#ff0033]/15 border border-[#ff0033]/30 text-[#ff5577]">+{interventiList.length - 3}</span>
                      )}
                    </div>
                  ) : (
                    <div className="font-display font-bold text-sm truncate">{m.intervento || m.descrizione || "Intervento"}</div>
                  )}
                  <div className="text-xs text-white/45">{cat.label} · {m.car?.marca} {m.car?.modello} <span className="font-mono">{m.car?.targa}</span></div>
                  {m.operatore_nome && <div className="text-[10px] text-white/40 mt-1 inline-flex items-center gap-1"><User size={9} />{m.operatore_nome}{m.officina ? ` @ ${m.officina}` : ""}</div>}
                </div>
                <div className="col-span-6 md:col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Data</div>
                  <div className="text-xs font-mono">{m.data}</div>
                  {m.km_intervento ? <div className="text-[10px] text-white/40">{m.km_intervento?.toLocaleString()} km</div> : null}
                </div>
                <div className="col-span-6 md:col-span-1">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Ricambi</div>
                  <div className="text-xs"><PackagePlus size={11} className="inline mr-1 text-white/40" />{ricambiCount}</div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Costo</div>
                  <div className="font-display text-lg font-extrabold neon-text">€ {(+(m.costo_totale || m.costo || 0)).toFixed(2)}</div>
                </div>
                <div className="col-span-6 md:col-span-1 flex justify-end gap-1">
                  <span className={`text-[9px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATO_BADGE[m.stato] || STATO_BADGE.programmata} hidden lg:inline-flex`}>{m.stato}</span>
                  <button onClick={() => startEdit(m)} data-testid={`edit-maint-${m.id}`} className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-white/10 text-white/60 hover:text-white"><Pencil size={12} /></button>
                  <button onClick={() => remove(m.id)} className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"><Trash2 size={12} /></button>
                </div>
              </div>
              {m.note && <div className="mt-2 pt-2 border-t border-white/5 text-xs text-white/50 italic">📝 {m.note}</div>}
            </motion.div>
          );
        })}
      </div>

      {/* === FORM MODAL CON CHIP CLICK === */}
      <AnimatePresence>
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <motion.form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            className="w-full max-w-5xl bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden max-h-[94vh] flex flex-col shadow-[0_0_120px_-20px_#ff0033]"
            data-testid="workshop-form">
            <div className="flex items-center justify-between px-7 py-5 border-b border-white/5 bg-gradient-to-r from-[#ff0033]/[0.08] via-transparent to-transparent">
              <div>
                <div className="font-display text-xl font-bold tracking-tight inline-flex items-center gap-2">
                  <Wrench size={18} className="text-[#ff0033]" /> {editId ? "Modifica intervento" : "Nuovo intervento — Clicca cosa è stato fatto"}
                </div>
                <div className="text-[11px] text-white/40 mt-1">Seleziona la categoria, clicca sugli interventi eseguiti (multi-select), aggiungi nota se serve</div>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-white/10"><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-auto p-6 space-y-5">
              {/* AI Scanner */}
              <DocumentScannerBox
                testIdPrefix="workshop-scanner"
                context="damages"
                variant="full"
                title="Scansione AI · Preventivo / Fattura officina"
                description="Carica il preventivo o la fattura dell'officina: l'AI estrae targa, descrizione e importo."
                onResult={(d) => {
                  const targa = d?.veicolo?.targa;
                  const desc = (d?.documento?.tipo === 'libretto' ? d?.note : (d?.note || d?.documenti_estratti?.[0]?.note)) || "";
                  const importo = parseFloat(d?.noleggio?.totale_eur || 0) || 0;
                  let car_id = form.car_id;
                  if (targa) {
                    const t = String(targa).toUpperCase().replace(/[\s-]/g, '');
                    const match = cars.find((c) => (c.targa || "").toUpperCase().replace(/[\s-]/g, '') === t);
                    if (match) car_id = match.id;
                  }
                  setForm({
                    ...form,
                    car_id,
                    descrizione: desc || form.descrizione,
                    costo_manodopera: importo || form.costo_manodopera,
                  });
                }}
              />

              {/* Veicolo + Categoria */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-1">
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Veicolo *</label>
                  <SearchableSelect
                    testid="form-car"
                    value={form.car_id}
                    onChange={(v) => setForm({ ...form, car_id: v })}
                    placeholder="Cerca marca, modello, targa…"
                    options={cars.map((c) => ({
                      value: c.id,
                      label: `${c.marca} ${c.modello}`,
                      extra: c.targa,
                    }))}
                  />
                </div>
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Settore officina</div>
                  <div className="grid grid-cols-4 md:grid-cols-7 gap-1.5 mt-1">
                    {WORKSHOP_CATEGORIES.map((cat) => {
                      const Ico = ICON_MAP[cat.icon] || Wrench;
                      const active = form.categoria === cat.value;
                      return (
                        <button key={cat.value} type="button" onClick={() => onCategoriaChange(cat.value)}
                          data-testid={`form-cat-${cat.value}`}
                          className={`p-2 rounded-lg border transition-all text-center ${active ? "border-[#ff0033] bg-[#ff0033]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
                          <Ico size={14} className="mx-auto" style={{ color: cat.color }} />
                          <div className="text-[9px] uppercase tracking-wider mt-1 truncate">{cat.label}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* === Selezione interventi a CHIP CLICK (search-first) === */}
              <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">
                    Cosa è stato fatto? <span className="text-[#ff5577]">Scrivi per cercare, clicca per selezionare</span>
                  </div>
                  {form.interventi_list.length > 0 && (
                    <span className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-[#ff0033]/15 border border-[#ff0033]/30 text-[#ff5577]" data-testid="chip-counter">
                      {form.interventi_list.length} sel.
                    </span>
                  )}
                </div>

                {/* Big search input */}
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#ff5577]" />
                  <input value={chipSearch} onChange={(e) => setChipSearch(e.target.value)}
                    data-testid="chip-search"
                    autoFocus
                    placeholder="es. braccetto, pastiglie, ammortizzatore…"
                    className="w-full bg-white/[0.03] border border-white/15 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:border-[#ff0033]/60 focus:outline-none placeholder-white/30" />
                  {chipSearch && (
                    <button type="button" onClick={() => setChipSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"><X size={12} /></button>
                  )}
                </div>

                <div className="space-y-2 max-h-[42vh] overflow-y-auto pr-1">
                  {Object.keys(filteredSections).length === 0 && (
                    <div className="text-center text-xs text-white/40 py-6">
                      {chipSearch ? `Nessun intervento trovato per "${chipSearch}"` : "Inizia a scrivere per cercare, oppure espandi le sezioni qui sotto"}
                    </div>
                  )}
                  {Object.entries(filteredSections).map(([section, list]) => {
                    // se c'è una search attiva → sezione aperta forzatamente
                    const open = chipSearchLower ? true : !!openSections[section];
                    const selectedInSection = list.filter((x) => form.interventi_list.includes(x)).length;
                    return (
                      <div key={section} className="rounded-lg bg-black/30 border border-white/5">
                        <button type="button" onClick={() => toggleSection(section)}
                          data-testid={`sec-${section}`}
                          className="w-full px-3 py-2 text-left flex items-center justify-between hover:bg-white/[0.02] rounded-t-lg">
                          <div className="text-[11px] uppercase tracking-widest font-bold text-white/80 inline-flex items-center gap-2">
                            <ChevronDown size={11} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
                            {section}
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedInSection > 0 && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#ff0033]/20 text-[#ff5577]">{selectedInSection}</span>
                            )}
                            <span className="text-[10px] text-white/35">{list.length} voci</span>
                          </div>
                        </button>
                        {open && (
                          <div className="px-3 pb-3 flex flex-wrap gap-1.5">
                            {list.map((intervento) => {
                              const selected = form.interventi_list.includes(intervento);
                              return (
                                <button key={intervento} type="button" onClick={() => toggleIntervento(intervento)}
                                  data-testid={`chip-${intervento.slice(0, 20).replace(/[^a-z0-9]/gi, '-')}`}
                                  className={`text-[11px] px-3 py-1.5 rounded-full border transition-all inline-flex items-center gap-1.5 ${
                                    selected
                                      ? "border-[#ff0033] bg-[#ff0033]/15 text-white shadow-[0_0_12px_-2px_#ff0033]"
                                      : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/30 hover:bg-white/[0.05]"
                                  }`}>
                                  {selected && <Check size={10} />}
                                  {intervento}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {form.interventi_list.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Interventi selezionati</div>
                    <div className="flex flex-wrap gap-1.5">
                      {form.interventi_list.map((iv) => (
                        <span key={iv} className="text-[11px] px-3 py-1 rounded-full bg-[#ff0033]/15 border border-[#ff0033]/30 text-white inline-flex items-center gap-2" data-testid={`sel-${iv.slice(0,20)}`}>
                          {iv}
                          <button type="button" onClick={() => toggleIntervento(iv)} className="hover:text-[#ff5577]"><X size={9} /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* === DATI ESSENZIALI === */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Data *</label>
                  <input type="date" required value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">KM</label>
                  <input type="number" value={form.km_intervento} onChange={(e) => setForm({ ...form, km_intervento: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40 inline-flex items-center gap-1"><User size={10} /> Operatore</label>
                  <input data-testid="form-operatore" value={form.operatore_nome} onChange={(e) => setForm({ ...form, operatore_nome: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none"
                    placeholder="Nome meccanico" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40 inline-flex items-center gap-1"><Building2 size={10} /> Officina</label>
                  <input value={form.officina} onChange={(e) => setForm({ ...form, officina: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="Se esterna" />
                </div>
              </div>

              {/* === NOTA OPZIONALE === */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Nota (opzionale)</label>
                <textarea rows={2} data-testid="form-note" value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none"
                  placeholder="Es. cliente segnala vibrazione a 100 km/h, riscontrate pastiglie usurate…" />
              </div>

              {/* === RICAMBI (collassabile, opzionale) === */}
              <details className="rounded-xl bg-white/[0.02] border border-white/5">
                <summary className="cursor-pointer p-3 text-[10px] uppercase tracking-widest text-white/45 inline-flex items-center gap-2">
                  <PackagePlus size={11} /> Ricambi utilizzati ({(form.ricambi || []).length}) · prezzo tracciato per fornitore
                </summary>
                <div className="px-4 pb-4 space-y-2">
                  {(form.ricambi || []).length === 0 && (
                    <div className="rounded-lg border border-dashed border-white/10 p-4 text-center text-xs text-white/40">Nessun ricambio</div>
                  )}
                  {(form.ricambi || []).map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`ricambio-${i}`}>
                      <div className="col-span-12 md:col-span-4">
                        <label className="text-[9px] uppercase tracking-widest text-white/40">Nome ricambio</label>
                        <input list={`ricambi-list-${i}`} value={r.nome} onChange={(e) => updateRicambio(i, "nome", e.target.value)}
                          className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:border-[#ff0033]/60 focus:outline-none" />
                        <datalist id={`ricambi-list-${i}`}>
                          {ricambiSuggestions.map((s) => <option key={s} value={s} />)}
                        </datalist>
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <label className="text-[9px] uppercase tracking-widest text-white/40">Codice</label>
                        <input value={r.codice} onChange={(e) => updateRicambio(i, "codice", e.target.value)}
                          className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs font-mono" />
                      </div>
                      <div className="col-span-3 md:col-span-1">
                        <label className="text-[9px] uppercase tracking-widest text-white/40">Qta</label>
                        <input type="number" step="0.01" value={r.quantita} onChange={(e) => updateRicambio(i, "quantita", e.target.value)}
                          className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
                      </div>
                      <div className="col-span-5 md:col-span-2">
                        <label className="text-[9px] uppercase tracking-widest text-white/40">€ unit.</label>
                        <input type="number" step="0.01" value={r.prezzo_unitario} onChange={(e) => updateRicambio(i, "prezzo_unitario", e.target.value)}
                          className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs" />
                      </div>
                      <div className="col-span-7 md:col-span-2">
                        <label className="text-[9px] uppercase tracking-widest text-white/40">Fornitore</label>
                        <input value={r.fornitore} onChange={(e) => updateRicambio(i, "fornitore", e.target.value)}
                          className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-2 py-1.5 text-xs" placeholder="es. Brembo" />
                      </div>
                      <div className="col-span-1 flex items-end">
                        <button type="button" onClick={() => removeRicambio(i)} className="p-1.5 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]"><Trash2 size={11} /></button>
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={addRicambio} data-testid="form-add-ricambio"
                    className="w-full text-[10px] uppercase tracking-widest px-3 py-2 rounded-lg bg-[#ff0033]/10 border border-dashed border-[#ff0033]/30 text-[#ff5577] hover:bg-[#ff0033]/20 inline-flex items-center justify-center gap-1">
                    <Plus size={11} /> Aggiungi ricambio
                  </button>
                </div>
              </details>

              {/* === COSTI === */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">€ Manodopera</label>
                  <input type="number" data-testid="form-manodopera" value={form.costo_manodopera} onChange={(e) => setForm({ ...form, costo_manodopera: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">€ Ricambi</label>
                  <input type="number" readOnly value={totaleRicambi.toFixed(2)}
                    className="mt-1 w-full bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2 text-sm text-white/60" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#ff5577]">€ Totale</label>
                  <input type="number" value={form.costo_totale} onChange={(e) => setForm({ ...form, costo_totale: e.target.value })}
                    data-testid="form-totale"
                    className="mt-1 w-full bg-[#ff0033]/[0.08] border border-[#ff0033]/30 rounded-lg px-3 py-2 text-sm font-bold" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Stato</label>
                  <select value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })}
                    className="mt-1 w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                    {["programmata", "in_corso", "completata", "annullata"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 px-7 py-4 border-t border-white/5 justify-between items-center bg-black/40">
              <div className="text-[11px] text-white/50">
                {form.interventi_list.length === 0 ? "⚠️ Seleziona almeno un intervento" : `✓ ${form.interventi_list.length} intervento/i pronti per la registrazione`}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10 hover:bg-white/10">Annulla</button>
                <button type="submit" data-testid="form-submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">{editId ? "Salva modifiche" : "Registra intervento"}</button>
              </div>
            </div>
          </motion.form>
        </div>
      )}
      </AnimatePresence>

      {/* PARTS HISTORY MODAL */}
      <AnimatePresence>
      {showPartsHistory && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowPartsHistory(false)}>
          <motion.div onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            className="w-full max-w-5xl bg-[#0a0a0a] border border-white/10 rounded-3xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-7 py-5 border-b border-white/5 bg-gradient-to-r from-[#ff0033]/[0.06] to-transparent">
              <div>
                <div className="font-display text-xl font-bold inline-flex items-center gap-2">
                  <BarChart3 size={18} className="text-[#ff0033]" /> Storico Prezzi Ricambi
                </div>
                <div className="text-[11px] text-white/40 mt-1">Variazioni prezzo, fornitori e frequenza acquisti</div>
              </div>
              <button onClick={() => setShowPartsHistory(false)} className="p-2 rounded-full hover:bg-white/10"><X size={16} /></button>
            </div>
            <div className="p-6 flex-1 overflow-auto space-y-4">
              <div className="flex gap-2">
                <input value={partsSearch} onChange={(e) => setPartsSearch(e.target.value)}
                  placeholder="Filtra per nome ricambio…"
                  data-testid="parts-search"
                  className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                <button onClick={loadPartsHistory} className="px-4 py-2 rounded-lg bg-[#ff0033]/15 border border-[#ff0033]/30 text-[#ff5577] text-xs uppercase tracking-widest">Cerca</button>
              </div>
              {!partsHistory ? (
                <div className="text-center text-white/40 py-8">Caricamento…</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
                      <div className="text-[10px] uppercase tracking-widest text-white/40">Acquisti totali</div>
                      <div className="font-display text-2xl font-bold mt-1">{partsHistory.total_records}</div>
                    </div>
                    <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
                      <div className="text-[10px] uppercase tracking-widest text-white/40">Ricambi diversi</div>
                      <div className="font-display text-2xl font-bold mt-1">{partsHistory.aggregated.length}</div>
                    </div>
                    <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4">
                      <div className="text-[10px] uppercase tracking-widest text-white/40">Variazioni rilevate</div>
                      <div className="font-display text-2xl font-bold mt-1">{partsHistory.aggregated.filter((a) => a.variazione_perc !== 0).length}</div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/5 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/45">
                        <tr>
                          <th className="text-left p-3">Ricambio</th>
                          <th className="text-right p-3">Acquisti</th>
                          <th className="text-right p-3">Min €</th>
                          <th className="text-right p-3">Medio €</th>
                          <th className="text-right p-3">Max €</th>
                          <th className="text-right p-3">Ultimo €</th>
                          <th className="text-right p-3">Variazione</th>
                          <th className="text-left p-3 hidden md:table-cell">Fornitori</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partsHistory.aggregated.length === 0 && (
                          <tr><td colSpan={8} className="text-center text-white/40 p-6">Nessun ricambio</td></tr>
                        )}
                        {partsHistory.aggregated.map((a, i) => {
                          const TrendIcon = a.variazione_perc > 0 ? TrendingUp : a.variazione_perc < 0 ? TrendingDown : MinusIcon;
                          const trendColor = a.variazione_perc > 0 ? "text-red-400" : a.variazione_perc < 0 ? "text-emerald-400" : "text-white/40";
                          return (
                            <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`part-row-${i}`}>
                              <td className="p-3 font-medium">{a.nome}</td>
                              <td className="p-3 text-right font-mono text-xs">{a.acquisti}</td>
                              <td className="p-3 text-right font-mono text-xs">{(+a.min_eur).toFixed(2)}</td>
                              <td className="p-3 text-right font-mono text-xs">{(+a.prezzo_medio_eur).toFixed(2)}</td>
                              <td className="p-3 text-right font-mono text-xs">{(+a.max_eur).toFixed(2)}</td>
                              <td className="p-3 text-right font-mono text-xs font-bold">{(+a.ultimo_prezzo).toFixed(2)}</td>
                              <td className={`p-3 text-right font-mono text-xs ${trendColor}`}>
                                <span className="inline-flex items-center gap-1 justify-end">
                                  <TrendIcon size={11} />
                                  {a.variazione_perc > 0 ? "+" : ""}{a.variazione_perc}%
                                </span>
                              </td>
                              <td className="p-3 text-xs text-white/50 hidden md:table-cell">{(a.fornitori || []).join(", ") || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
      </AnimatePresence>
    </div>
  );
}
